// Slack/Mattermost-style notification sounds, synthesised with WebAudio so we
// ship no audio assets. Browsers keep an AudioContext suspended until the user
// has interacted with the page — we resume lazily and fail silently, so the
// first ping after a cold load may be skipped (same as Slack in a fresh tab).
//
// Mute switch: localStorage.setItem("hrms-chat-sound", "off")

let ctx = null;

function ensureCtx() {
  if (ctx === null) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = AC ? new AC() : false;
    } catch (_) {
      ctx = false;
    }
  }
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx || null;
}

// First real user gesture unlocks audio for the session. iOS Safari is the
// strict one: the context must be created/resumed INSIDE a gesture handler
// (historically touchend), and playing a silent buffer inside that gesture is
// the reliable way to fully unlock output. Also note: iOS routes WebAudio
// through the RINGER channel — with the hardware mute switch on silent, web
// pages cannot make notification sounds at all (system push still can).
if (typeof window !== "undefined") {
  const unlock = () => {
    const c = ensureCtx();
    if (c) {
      try {
        // Silent one-sample buffer — the canonical iOS audio unlock.
        const buf = c.createBuffer(1, 1, 22050);
        const src = c.createBufferSource();
        src.buffer = buf;
        src.connect(c.destination);
        src.start(0);
      } catch (_) {}
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("touchend", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("touchend", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  // iOS suspends the context when the tab is backgrounded; resume on return.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && ctx && ctx.state !== "running") {
      ctx.resume().catch(() => {});
    }
  });
}

function tone(c, freq, at, dur, peak, type = "sine") {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.linearRampToValueAtTime(peak, at + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + dur + 0.05);
}

/**
 * kind: "message" (DM / channel / bell) → single soft pop
 *       "mention"                       → brighter two-tone ping
 */
export function playNotifySound(kind = "message") {
  try {
    if (localStorage.getItem("hrms-chat-sound") === "off") return;
    const c = ensureCtx();
    if (!c) return;
    // iOS reports "interrupted" after calls/Siri — try a resume before giving up.
    if (c.state !== "running") {
      c.resume().catch(() => {});
      if (c.state !== "running") return;
    }
    const t = c.currentTime + 0.01;
    if (kind === "mention") {
      tone(c, 880, t, 0.16, 0.09, "triangle"); // A5
      tone(c, 1318.5, t + 0.11, 0.22, 0.08, "triangle"); // E6
    } else {
      tone(c, 659.25, t, 0.14, 0.06, "sine"); // E5 soft pop
      tone(c, 987.77, t + 0.02, 0.1, 0.025, "sine"); // faint sparkle
    }
    // Expose for tests: last sound played + when.
    window.__lastNotifySound = { kind, at: Date.now() };
  } catch (_) {
    /* sound is best-effort, never break the app for it */
  }
}
